import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Print,
  Download,
  DragIndicator,
  Reorder,
  LinkedIn as LinkedInIcon
} from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ComputerIcon from "@mui/icons-material/Computer";

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
  colorOptions: [
    { value: '#4a4a4a', label: 'Dark Gray' },
    { value: '#2196f3', label: 'Blue' },
    { value: '#4caf50', label: 'Green' },
    { value: '#ff9800', label: 'Orange' },
    { value: '#9c27b0', label: 'Purple' },
    { value: '#f44336', label: 'Red' }
  ]
};

const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const defaultData = {
  firstName: "John",
  lastName: "Doe",
  currentPosition: "Full Stack Developer",
  phone: "+91 9876543210",
  email: "johndoe@email.com",
  city: "Chennai, India",
  website: "www.johndoe.com",
  profile:
    "Highly motivated Full Stack Developer with experience in building scalable MERN stack applications. Passionate about problem solving and creating user-friendly solutions.",
};

const defaultLeftSectionOrder = [
  'about',
  'contact',
  'education',
  'skills'
];

const defaultRightSectionOrder = [
  'workExperience',
  'certifications',
  'accomplishments',
    'websites',
  'volunteering',
  'interests',
  'languages',
  'additionalInfo',
  'references'
];

const Resume36 = ({
  color = "#4a4a4a",
  font = "Inter, Roboto, sans-serif",
  fontSize = "12.5px",
  headingSize = "13px",
  fontStyle = "normal",
  sectionSpacing = 10,
  paragraphSpacing = 30,
  lineSpacing = 20,
  topBottomMargin = 20,
  sideMargins = 20,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true,
  linkedIn = '',
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  
  const data = { 
    ...defaultData, 
    ...formData,
    linkedIn: linkedIn || formData.linkedIn || '',
  };

  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});
  
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const [sidebarPages, setSidebarPages] = useState([]);
  const rightContentRef = useRef(null);
  const sidebarContentRef = useRef(null);

  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // Global font style
  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
  };

  const getTextStyle = (additionalStyles = {}) => ({
    ...globalFontStyle,
    wordBreak: "break-word",
    overflowWrap: "break-word",
    ...additionalStyles
  });

  useEffect(() => {
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) setEducationEntries(JSON.parse(storedEntries));

    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) setSavedSkills(JSON.parse(storedSkills));

    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) setSavedSummary(storedSummaries[storedSummaries.length - 1]);

    setSavedAdditionalInfo(JSON.parse(localStorage.getItem("additionalInfo")) || []);
    setSavedLanguages(JSON.parse(localStorage.getItem("languagesList")) || []);
    setSavedAccomplishments(JSON.parse(localStorage.getItem("accomplishmentsList")) || []);
    setSavedCertifications(JSON.parse(localStorage.getItem("certificationsList")) || []);
    setSavedReferences(JSON.parse(localStorage.getItem("referencesList")) || []);
    setSoftwareSkills(JSON.parse(localStorage.getItem("softwareSkills")) || []);
    setSavedVolunteering(JSON.parse(localStorage.getItem("volunteering")) || []);
    setSavedInterests(JSON.parse(localStorage.getItem("interests")) || []);
    setSavedWebsites(JSON.parse(localStorage.getItem("websites")) || []);

    const userId = localStorage.getItem("userId");
    let sectionsData = {};

    if (userId) {
      const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
      currentCustomSections.forEach(sectionName => {
        const userCustomKey = `custom_${userId}_${sectionName}`;
        try {
          const customSectionData = localStorage.getItem(userCustomKey);
          if (customSectionData) {
            const parsedData = JSON.parse(customSectionData);
            const items = (parsedData.items || []).filter(item => item && item.trim() !== '');
            if (items.length > 0) sectionsData[sectionName] = items;
          }
        } catch (error) {
          console.error(`Error loading custom section ${sectionName}:`, error);
        }
      });
    }

    setCustomSections(sectionsData);

    const savedLeft = localStorage.getItem("resume36LeftSectionOrder");
    const savedRight = localStorage.getItem("resume36RightSectionOrder");

    if (savedLeft && savedRight) {
      setLeftSectionOrder(JSON.parse(savedLeft));
      setRightSectionOrder(JSON.parse(savedRight));
    } else {
      const customIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
    }
  }, []);

  useEffect(() => {
    if (leftSectionOrder.length > 0) localStorage.setItem("resume36LeftSectionOrder", JSON.stringify(leftSectionOrder));
  }, [leftSectionOrder]);

  useEffect(() => {
    if (rightSectionOrder.length > 0) localStorage.setItem("resume36RightSectionOrder", JSON.stringify(rightSectionOrder));
  }, [rightSectionOrder]);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId) {
      const currentCustomIds = Object.keys(customSections).map(name => `custom_${name}`);
      const filtered = rightSectionOrder.filter(s => !s.startsWith('custom_'));
      const newRight = [...filtered, ...currentCustomIds];
      if (JSON.stringify(newRight) !== JSON.stringify(rightSectionOrder)) {
        setRightSectionOrder(newRight);
        localStorage.setItem("resume36RightSectionOrder", JSON.stringify(newRight));
      }
    }
  }, [customSections]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // Drag and drop handlers
  const handleLeftDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side: 'left', sectionId: leftSectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleRightDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side: 'right', sectionId: rightSectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index, side) => {
    e.preventDefault();
    setDragOverIndex(index);
    setDragOverSide(side);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleLeftDrop = (e, targetIndex) => {
    e.preventDefault();
    const dropData = JSON.parse(e.dataTransfer.getData('text/plain'));
    const { index: sourceIndex, side: sourceSide, sectionId } = dropData;

    if (sourceSide === 'left' && sourceIndex !== targetIndex) {
      const newOrder = [...leftSectionOrder];
      const [moved] = newOrder.splice(sourceIndex, 1);
      newOrder.splice(targetIndex, 0, moved);
      setLeftSectionOrder(newOrder);
    } else if (sourceSide === 'right') {
      const newLeft = [...leftSectionOrder];
      const newRight = [...rightSectionOrder];
      newRight.splice(sourceIndex, 1);
      newLeft.splice(targetIndex, 0, sectionId);
      setLeftSectionOrder(newLeft);
      setRightSectionOrder(newRight);
    }

    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleRightDrop = (e, targetIndex) => {
    e.preventDefault();
    const dropData = JSON.parse(e.dataTransfer.getData('text/plain'));
    const { index: sourceIndex, side: sourceSide, sectionId } = dropData;

    if (sourceSide === 'right' && sourceIndex !== targetIndex) {
      const newOrder = [...rightSectionOrder];
      const [moved] = newOrder.splice(sourceIndex, 1);
      newOrder.splice(targetIndex, 0, moved);
      setRightSectionOrder(newOrder);
    } else if (sourceSide === 'left') {
      const newLeft = [...leftSectionOrder];
      const newRight = [...rightSectionOrder];
      newLeft.splice(sourceIndex, 1);
      newRight.splice(targetIndex, 0, sectionId);
      setLeftSectionOrder(newLeft);
      setRightSectionOrder(newRight);
    }

    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetLeftSectionOrder = () => setLeftSectionOrder(defaultLeftSectionOrder);
  const resetRightSectionOrder = () => {
    const customIds = Object.keys(customSections).map(name => `custom_${name}`);
    setRightSectionOrder([...defaultRightSectionOrder, ...customIds]);
  };
  const resetAllSectionOrder = () => {
    resetLeftSectionOrder();
    resetRightSectionOrder();
  };

  // Resume36 specific Section Title component - pill/badge style
  const SectionTitle = ({ title, spacing = sectionSpacing }) => (
    <Box style={{
      border: `1px solid ${color}`,
      borderRadius: "20px",
      paddingLeft: "20px",
      paddingRight: "20px",
      paddingTop: "4px",
      paddingBottom: "4px",
      display: "inline-block",
      marginBottom: `${spacing}px`,
      maxWidth: "100%",
    }}>
      <Typography component="div" style={{
        fontSize: headingSize,
        fontWeight: 700,
        letterSpacing: 1,
        textTransform: "uppercase",
        color: color,
        fontFamily: font,
        fontStyle: fontStyle,
        wordBreak: "break-word",
      }}>
        {title}
      </Typography>
    </Box>
  );

  // Common text styles with word break
  const textStyle = {
    ...globalFontStyle,
    wordBreak: "break-word",
    overflowWrap: "break-word",
  };

  const renderSidebarSections = () => {
    return leftSectionOrder.map(sectionId => {
      switch (sectionId) {
        case 'about':
          return (savedSummary || data.profile) ? (
            <div key="about" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionTitle title="About Me" spacing={sectionSpacing} />
              <Typography sx={{ ...textStyle, mb: `${paragraphSpacing}px`, color: "#333" }}>
                {savedSummary || data.profile}
              </Typography>
            </div>
          ) : null;

        case 'contact':
          return (data.phone || data.email || data.city || data.linkedIn) ? (
            <div key="contact" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionTitle title="Contact" spacing={sectionSpacing} />
              <Stack spacing={0.8} sx={{ mb: `${paragraphSpacing}px` }}>
                {data.phone && (
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                    <PhoneIcon sx={{ fontSize: 14, mt: 0.3, color: color, flexShrink: 0 }} />
                    <Typography sx={{ ...textStyle }}>{data.phone}</Typography>
                  </Box>
                )}
                {data.email && (
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                    <EmailIcon sx={{ fontSize: 14, mt: 0.3, color: color, flexShrink: 0 }} />
                    <Typography sx={{ ...textStyle, wordBreak: "break-all" }}>{data.email}</Typography>
                  </Box>
                )}
                {data.city && (
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                    <LocationOnIcon sx={{ fontSize: 14, mt: 0.3, color: color, flexShrink: 0 }} />
                    <Typography sx={{ ...textStyle }}>{data.city}</Typography>
                  </Box>
                )}
                {data.linkedIn && (
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                    <LinkedInIcon sx={{ fontSize: 14, mt: 0.3, color: color, flexShrink: 0 }} />
                    <Typography sx={{ ...textStyle, wordBreak: "break-all" }}>{data.linkedIn}</Typography>
                  </Box>
                )}
              </Stack>
            </div>
          ) : null;

        case 'education':
          return educationEntries.length > 0 ? (
            <div key="education" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionTitle title="Education" spacing={sectionSpacing} />
              <Box sx={{ mb: `${paragraphSpacing}px` }}>
                {educationEntries.map((edu) => {
                  const institutionName = edu.educationLevel === "school" ? edu.schoolName : edu.collegeName;
                  const degreeName = edu.educationLevel === "school" ? edu.standard : edu.degreeName;
                  return (
                    <Box key={edu.id} sx={{ mb: `${paragraphSpacing}px` }}>
                      <Typography sx={{ ...textStyle, fontWeight: 600, color: "#333" }}>{degreeName}</Typography>
                      <Typography sx={{ ...textStyle, fontSize: "12px", color: "#666" }}>
                        {institutionName} ({formatDate(edu.gradMonth, edu.gradYear)})
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </div>
          ) : null;

        case 'skills':
          return (savedSkills.length > 0 || softwareSkills.length > 0) ? (
            <div key="skills" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionTitle title="Skills" spacing={sectionSpacing} />
              <Box component="ul" sx={{ pl: 2.5, fontSize: fontSize, color: "#333", listStyleType: "disc", m: 0, mb: `${paragraphSpacing}px` }}>
                {savedSkills.map((skill, index) => (
                  <Box component="li" key={index} sx={{ mb: "3px", ...globalFontStyle, wordBreak: "break-word" }}>
                    {skill.name} {skill.rating ? `(${skill.rating}%)` : ""}
                  </Box>
                ))}
                {softwareSkills.map((skill, index) => (
                  <Box component="li" key={`sw-${index}`} sx={{ mb: "3px", ...globalFontStyle, wordBreak: "break-word" }}>
                    {skill.name} {skill.level ? `(${skill.level}%)` : ""}
                  </Box>
                ))}
              </Box>
            </div>
          ) : null;

        default:
          return renderRightContentSectionsForSidebar(sectionId);
      }
    }).filter(Boolean);
  };

  const renderRightContentSectionsForSidebar = (sectionId) => {
    switch (sectionId) {
      case 'workExperience':
        return workExperiences && workExperiences.length > 0 ? (
          <div key="workExperience" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionTitle title="Experience" spacing={sectionSpacing} />
            {workExperiences.map((work, i) => (
              <Box key={i} sx={{ mb: `${paragraphSpacing}px` }}>
                <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={0.5}>
                  <Typography sx={{ ...textStyle, fontWeight: 600, color: "#333" }}>{work.jobTitle}</Typography>
                  <Typography sx={{ ...textStyle, color: color }}>
                    {formatDate(work.startMonth, work.startYear)} – {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                  </Typography>
                </Box>
                <Typography sx={{ ...textStyle, color: "gray", mb: 0.5 }}>{work.employer}</Typography>
                {work.description && (
                  <Box
                    sx={{ "& *": { fontFamily: font, fontSize: fontSize, fontStyle: fontStyle, lineHeight: `${lineSpacing}px`, wordBreak: "break-word" } }}
                    dangerouslySetInnerHTML={{ __html: work.description.replace(/\n/g, "<br/>") }}
                  />
                )}
                <Divider sx={{ mt: 1, borderColor: color, opacity: 0.3 }} />
              </Box>
            ))}
          </div>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <div key="certifications" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionTitle title="Certifications" spacing={sectionSpacing} />
            {savedCertifications.map((cert, index) => (
              <Box key={index} sx={{ mb: `${paragraphSpacing}px` }}>
                <Typography sx={{ ...textStyle, fontWeight: 600, color: "#333" }}>{cert.name}</Typography>
                <Typography sx={{ ...textStyle, color: "#666" }}>{cert.provider} {cert.year && `(${cert.year})`}</Typography>
                <Divider sx={{ mt: 1, borderColor: color, opacity: 0.3 }} />
              </Box>
            ))}
          </div>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <div key="accomplishments" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionTitle title="Accomplishments" spacing={sectionSpacing} />
            <Box component="ul" sx={{ pl: 2.5, m: 0, listStyleType: "disc" }}>
              {savedAccomplishments.map((acc, index) => (
                <Box component="li" key={index} sx={{ mb: "3px", ...globalFontStyle, wordBreak: "break-word" }}>{acc}</Box>
              ))}
            </Box>
          </div>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <div key="volunteering" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionTitle title="Volunteering" spacing={sectionSpacing} />
            {savedVolunteering.map((vol, index) => (
              <Box key={index} sx={{ mb: `${paragraphSpacing}px` }}>
                <Typography sx={{ ...textStyle, fontWeight: 600, color: "#333" }}>{vol.subtopic}</Typography>
                <Typography sx={{ ...textStyle, color: '#666', fontStyle: 'italic' }}>{vol.fromDate} - {vol.toDate}</Typography>
                <Typography sx={{ ...textStyle, color: "#333" }}>{vol.content}</Typography>
                <Divider sx={{ mt: 1, borderColor: color, opacity: 0.3 }} />
              </Box>
            ))}
          </div>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <div key="interests" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionTitle title="Interests" spacing={sectionSpacing} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: `${paragraphSpacing}px` }}>
              {savedInterests.map((interest, index) => (
                <Box key={index} sx={{ border: `1px solid ${color}`, borderRadius: "15px", px: 1.5, py: 0.4 }}>
                  <Typography sx={{ ...textStyle, color: color }}>{interest}</Typography>
                </Box>
              ))}
            </Box>
          </div>
        ) : null;

      case 'languages':
        return savedLanguages.length > 0 ? (
          <div key="languages" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionTitle title="Languages" spacing={sectionSpacing} />
            <Box sx={{ mb: `${paragraphSpacing}px` }}>
              {savedLanguages.map((lang, index) => (
                <Box key={index} sx={{ mb: 0.8 }}>
                  <Typography sx={{ ...textStyle, color: "#333" }}>
                    {lang.name} {lang.level && <span style={{ color: color }}>({lang.level})</span>}
                  </Typography>
                </Box>
              ))}
            </Box>
          </div>
        ) : null;

              // ── WEBSITES SECTION ──
      case 'websites':
        return savedWebsites.length > 0 ? (
          <div key="websites" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionTitle title="Websites" spacing={sectionSpacing} />
            <Box sx={{ mb: `${paragraphSpacing}px` }}>
              {savedWebsites.map((site, index) => {
                const siteName = typeof site === 'string' ? site : (site.name || site.url || site);
                const siteUrl = typeof site === 'string' ? site : (site.url || site);
                return (
                  <Box key={index} sx={{ mb: 0.8, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <ComputerIcon sx={{ fontSize: 14, color: color, flexShrink: 0 }} />
                    <Typography sx={{ ...textStyle }}>
                      <a 
                        href={siteUrl && siteUrl.startsWith('http') ? siteUrl : `https://${siteUrl || siteName}`}
                        style={{ 
                          color: color, 
                          fontFamily: font, 
                          fontStyle: fontStyle,
                          textDecoration: 'none',
                          fontSize: fontSize,
                          lineHeight: `${lineSpacing}px`
                        }} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        {siteName}
                      </a>
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </div>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <div key="additionalInfo" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionTitle title="Additional Information" spacing={sectionSpacing} />
            <Box component="ul" sx={{ pl: 2.5, m: 0, listStyleType: "disc" }}>
              {savedAdditionalInfo.map((info, index) => (
                <Box component="li" key={index} sx={{ mb: "3px", ...globalFontStyle, wordBreak: "break-word" }}>{info}</Box>
              ))}
            </Box>
          </div>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <div key="references" className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionTitle title="References" spacing={sectionSpacing} />
            <Grid container spacing={1.5}>
              {savedReferences.map((ref, index) => (
                <Grid item xs={6} key={index}>
                  <Typography sx={{ ...textStyle, fontWeight: 600, color: color }}>{ref}</Typography>
                </Grid>
              ))}
            </Grid>
          </div>
        ) : null;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const validItems = (customSections[customSectionName] || []).filter(item => item && item.trim() !== '');
          if (validItems.length > 0) {
            return (
              <div key={sectionId} className="sidebar-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
                <SectionTitle title={customSectionName.replace(/_/g, ' ').toUpperCase()} spacing={sectionSpacing} />
                <Box component="ul" sx={{ pl: 2.5, m: 0, listStyleType: "disc" }}>
                  {validItems.map((item, i) => (
                    <Box component="li" key={i} sx={{ mb: "3px", ...globalFontStyle, wordBreak: "break-word" }}>{item}</Box>
                  ))}
                </Box>
              </div>
            );
          }
        }
        return null;
    }
  };

  const renderRightContentSections = () => {
    return rightSectionOrder.map(sectionId => {
      switch (sectionId) {
        case 'workExperience':
          return workExperiences && workExperiences.length > 0 ? (
            <div key="workExperience" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionTitle title="Experience" spacing={sectionSpacing} />
              {workExperiences.map((work, i) => (
                <Box key={i} sx={{ mb: `${paragraphSpacing}px` }}>
                  <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={0.5}>
                    <Typography sx={{ ...textStyle, fontWeight: 600, color: "#333" }}>{work.jobTitle}</Typography>
                    <Typography sx={{ ...textStyle, color: color }}>
                      {formatDate(work.startMonth, work.startYear)} – {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                    </Typography>
                  </Box>
                  <Typography sx={{ ...textStyle, color: "gray", mb: 0.5 }}>{work.employer}</Typography>
                  {work.description && (
                    <Box
                      sx={{ "& *": { fontFamily: font, fontSize: fontSize, fontStyle: fontStyle, lineHeight: `${lineSpacing}px`, wordBreak: "break-word" } }}
                      dangerouslySetInnerHTML={{ __html: work.description.replace(/\n/g, "<br/>") }}
                    />
                  )}
                  <Divider sx={{ mt: 1, borderColor: color, opacity: 0.3 }} />
                </Box>
              ))}
            </div>
          ) : null;

        case 'certifications':
          return savedCertifications.length > 0 ? (
            <div key="certifications" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionTitle title="Certifications" spacing={sectionSpacing} />
              {savedCertifications.map((cert, index) => (
                <Box key={index} sx={{ mb: `${paragraphSpacing}px` }}>
                  <Typography sx={{ ...textStyle, fontWeight: 600, color: "#333" }}>{cert.name}</Typography>
                  <Typography sx={{ ...textStyle, color: "#666" }}>{cert.provider} {cert.year && `(${cert.year})`}</Typography>
                  <Divider sx={{ mt: 1, borderColor: color, opacity: 0.3 }} />
                </Box>
              ))}
            </div>
          ) : null;

        case 'accomplishments':
          return savedAccomplishments.length > 0 ? (
            <div key="accomplishments" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionTitle title="Accomplishments" spacing={sectionSpacing} />
              <Box component="ul" sx={{ pl: 2.5, m: 0, listStyleType: "disc" }}>
                {savedAccomplishments.map((acc, index) => (
                  <Box component="li" key={index} sx={{ mb: "3px", ...globalFontStyle, wordBreak: "break-word" }}>{acc}</Box>
                ))}
              </Box>
            </div>
          ) : null;

        case 'volunteering':
          return savedVolunteering.length > 0 ? (
            <div key="volunteering" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionTitle title="Volunteering" spacing={sectionSpacing} />
              {savedVolunteering.map((vol, index) => (
                <Box key={index} sx={{ mb: `${paragraphSpacing}px` }}>
                  <Typography sx={{ ...textStyle, fontWeight: 600, color: "#333" }}>{vol.subtopic}</Typography>
                  <Typography sx={{ ...textStyle, color: '#666', fontStyle: 'italic' }}>{vol.fromDate} - {vol.toDate}</Typography>
                  <Typography sx={{ ...textStyle, color: "#333" }}>{vol.content}</Typography>
                  <Divider sx={{ mt: 1, borderColor: color, opacity: 0.3 }} />
                </Box>
              ))}
            </div>
          ) : null;

        case 'interests':
          return savedInterests.length > 0 ? (
            <div key="interests" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionTitle title="Interests" spacing={sectionSpacing} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: `${paragraphSpacing}px` }}>
                {savedInterests.map((interest, index) => (
                  <Box key={index} sx={{ border: `1px solid ${color}`, borderRadius: "15px", px: 1.5, py: 0.4 }}>
                    <Typography sx={{ ...textStyle, color: color }}>{interest}</Typography>
                  </Box>
                ))}
              </Box>
            </div>
          ) : null;

           case 'languages':
          return savedLanguages.length > 0 ? (
            <div key="languages" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionTitle title="Languages" spacing={sectionSpacing} />
              <Box sx={{ mb: `${paragraphSpacing}px` }}>
                {savedLanguages.map((lang, index) => (
                  <Box key={index} sx={{ mb: 0.8 }}>
                    <Typography sx={{ ...textStyle, color: "#333" }}>
                      {lang.name} {lang.level && <span style={{ color: color }}>({lang.level})</span>}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </div>
          ) : null;

        case 'websites':
          return savedWebsites.length > 0 ? (
            <div key="websites" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionTitle title="Websites" spacing={sectionSpacing} />
              <Box sx={{ mb: `${paragraphSpacing}px` }}>
                {savedWebsites.map((site, index) => {
                  const siteName = typeof site === 'string' ? site : (site.name || site.url || site);
                  const siteUrl = typeof site === 'string' ? site : (site.url || site);
                  return (
                    <Box key={index} sx={{ mb: 0.8, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <ComputerIcon sx={{ fontSize: 14, color: color, flexShrink: 0 }} />
                      <Typography sx={{ ...textStyle }}>
                        <a 
                          href={siteUrl && siteUrl.startsWith('http') ? siteUrl : `https://${siteUrl || siteName}`}
                          style={{ 
                            fontFamily: font, 
                            fontStyle: fontStyle,
                            textDecoration: 'none',
                            fontSize: fontSize,
                            lineHeight: `${lineSpacing}px`
                          }} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          {siteName}
                        </a>
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </div>
          ) : null;
        case 'additionalInfo':
          return savedAdditionalInfo.length > 0 ? (
            <div key="additionalInfo" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionTitle title="Additional Information" spacing={sectionSpacing} />
              <Box component="ul" sx={{ pl: 2.5, m: 0, listStyleType: "disc" }}>
                {savedAdditionalInfo.map((info, index) => (
                  <Box component="li" key={index} sx={{ mb: "3px", ...globalFontStyle, wordBreak: "break-word" }}>{info}</Box>
                ))}
              </Box>
            </div>
          ) : null;

        case 'references':
          return savedReferences.length > 0 ? (
            <div key="references" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
              <SectionTitle title="References" spacing={sectionSpacing} />
              <Grid container spacing={1.5}>
                {savedReferences.map((ref, index) => (
                  <Grid item xs={6} key={index}>
                    <Typography sx={{ ...textStyle, fontWeight: 600, color: color }}>{ref}</Typography>
                  </Grid>
                ))}
              </Grid>
            </div>
          ) : null;

        default:
          return renderLeftSectionsForRight(sectionId);
      }
    }).filter(Boolean);
  };

  const renderLeftSectionsForRight = (sectionId) => {
    switch (sectionId) {
      case 'about':
        return (savedSummary || data.profile) ? (
          <div key="about" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionTitle title="About Me" spacing={sectionSpacing} />
            <Typography sx={{ ...textStyle, mb: `${paragraphSpacing}px`, color: "#333" }}>{savedSummary || data.profile}</Typography>
          </div>
        ) : null;

      case 'contact':
        return (data.phone || data.email || data.city || data.linkedIn) ? (
          <div key="contact" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionTitle title="Contact" spacing={sectionSpacing} />
            <Stack spacing={0.8} sx={{ mb: `${paragraphSpacing}px` }}>
              {data.phone && (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 14, mt: 0.3, color: color, flexShrink: 0 }} />
                  <Typography sx={{ ...textStyle }}>{data.phone}</Typography>
                </Box>
              )}
              {data.email && (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 14, mt: 0.3, color: color, flexShrink: 0 }} />
                  <Typography sx={{ ...textStyle, wordBreak: "break-all" }}>{data.email}</Typography>
                </Box>
              )}
              {data.city && (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: 14, mt: 0.3, color: color, flexShrink: 0 }} />
                  <Typography sx={{ ...textStyle }}>{data.city}</Typography>
                </Box>
              )}
              {data.linkedIn && (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                  <LinkedInIcon sx={{ fontSize: 14, mt: 0.3, color: color, flexShrink: 0 }} />
                  <Typography sx={{ ...textStyle, wordBreak: "break-all" }}>{data.linkedIn}</Typography>
                </Box>
              )}
            </Stack>
          </div>
        ) : null;

      case 'education':
        return educationEntries.length > 0 ? (
          <div key="education" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionTitle title="Education" spacing={sectionSpacing} />
            <Box sx={{ mb: `${paragraphSpacing}px` }}>
              {educationEntries.map((edu) => {
                const institutionName = edu.educationLevel === "school" ? edu.schoolName : edu.collegeName;
                const degreeName = edu.educationLevel === "school" ? edu.standard : edu.degreeName;
                return (
                  <Box key={edu.id} sx={{ mb: `${paragraphSpacing}px` }}>
                    <Typography sx={{ ...textStyle, fontWeight: 600, color: "#333" }}>{degreeName}</Typography>
                    <Typography sx={{ ...textStyle, fontSize: "12px", color: "#666" }}>
                      {institutionName} ({formatDate(edu.gradMonth, edu.gradYear)})
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </div>
        ) : null;

      case 'skills':
        return (savedSkills.length > 0 || softwareSkills.length > 0) ? (
          <div key="skills" className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
            <SectionTitle title="Skills" spacing={sectionSpacing} />
            <Box component="ul" sx={{ pl: 2.5, m: 0, listStyleType: "disc", mb: `${paragraphSpacing}px` }}>
              {savedSkills.map((skill, index) => (
                <Box component="li" key={index} sx={{ mb: "3px", ...globalFontStyle, wordBreak: "break-word" }}>
                  {skill.name} {skill.rating ? `(${skill.rating}%)` : ""}
                </Box>
              ))}
              {softwareSkills.map((skill, index) => (
                <Box component="li" key={`sw-${index}`} sx={{ mb: "3px", ...globalFontStyle, wordBreak: "break-word" }}>
                  {skill.name} {skill.level ? `(${skill.level}%)` : ""}
                </Box>
              ))}
            </Box>
          </div>
        ) : null;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const validItems = (customSections[customSectionName] || []).filter(item => item && item.trim() !== '');
          if (validItems.length > 0) {
            return (
              <div key={sectionId} className="resume-section" style={{ ...globalFontStyle, marginBottom: `${sectionSpacing}px` }}>
                <SectionTitle title={customSectionName.replace(/_/g, ' ').toUpperCase()} spacing={sectionSpacing} />
                <Box component="ul" sx={{ pl: 2.5, m: 0, listStyleType: "disc" }}>
                  {validItems.map((item, i) => (
                    <Box component="li" key={i} sx={{ mb: "3px", ...globalFontStyle, wordBreak: "break-word" }}>{item}</Box>
                  ))}
                </Box>
              </div>
            );
          }
        }
        return null;
    }
  };

  const getSectionTitle = (sectionId) => {
    const titles = {
      'about': 'About Me', 'contact': 'Contact', 'education': 'Education', 'skills': 'Skills',
      'languages': 'Languages', 'workExperience': 'Work Experience', 'certifications': 'Certifications',
      'accomplishments': 'Accomplishments', 'volunteering': 'Volunteering', 'interests': 'Interests',
      'additionalInfo': 'Additional Information', 'references': 'References', 'websites': 'Websites',
    };
    if (sectionId.startsWith('custom_')) return sectionId.replace('custom_', '').replace(/_/g, ' ').toUpperCase();
    return titles[sectionId] || sectionId;
  };

  const renderDragPanel = (side, sections) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <DragIndicator sx={{ mr: 1 }} />
        {side === 'left' ? 'Left Sidebar' : 'Right Content'} Sections ({sections.length})
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
        {sections.length > 0 ? sections.map((sectionId, index) => (
          <Paper
            key={sectionId}
            draggable
            onDragStart={(e) => side === 'left' ? handleLeftDragStart(e, index) : handleRightDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index, side)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => side === 'left' ? handleLeftDrop(e, index) : handleRightDrop(e, index)}
            onDragEnd={handleDragEnd}
            sx={{
              p: 2, mb: 1, display: 'flex', alignItems: 'center',
              backgroundColor: dragOverIndex === index && dragOverSide === side ? '#f0f0f0' : 'white',
              boxShadow: dragOverIndex === index && dragOverSide === side ? 2 : 1,
              cursor: 'grab', '&:active': { cursor: 'grabbing' }, transition: 'all 0.2s ease',
            }}
          >
            <DragIndicator color="action" />
            <Typography sx={{ ml: 1, flexGrow: 1 }}>{getSectionTitle(sectionId)}</Typography>
            <Typography variant="body2" color="textSecondary">{index + 1}</Typography>
          </Paper>
        )) : (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
            No sections. Drag from the other side.
          </Typography>
        )}
      </Box>
      <Button onClick={side === 'left' ? resetLeftSectionOrder : resetRightSectionOrder} variant="outlined" size="small" sx={{ mt: 1 }}>
        Reset {side === 'left' ? 'Left' : 'Right'} Order
      </Button>
    </Box>
  );

  // Pagination
  const paginateSidebarContent = useCallback(() => {
    if (!sidebarContentRef.current) return;
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const headerHeight = 150;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - headerHeight;
    const sections = Array.from(sidebarContentRef.current.querySelectorAll('.sidebar-section'));
    
    let newSidebarPages = [];
    let currentPageSections = [];
    let currentHeight = 0;
    
    sections.forEach((section, index) => {
      const sectionHeight = section.offsetHeight;
      if (currentHeight + sectionHeight > availableHeight) {
        if (currentPageSections.length > 0) newSidebarPages.push([...currentPageSections]);
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
      if (index === sections.length - 1 && currentPageSections.length > 0) newSidebarPages.push([...currentPageSections]);
    });
    
    if (newSidebarPages.length === 0) newSidebarPages.push([]);
    setSidebarPages(newSidebarPages);
  }, [page.height, topBottomMargin, leftSectionOrder, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font, color, customSections]);

  const paginateMainContent = useCallback(() => {
    if (!rightContentRef.current) return;
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const headerHeight = 150;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - headerHeight;
    const sections = Array.from(rightContentRef.current.querySelectorAll('.resume-section'));
    
    let newPages = [];
    let currentPageSections = [];
    let currentHeight = 0;
    
    sections.forEach((section, index) => {
      const sectionHeight = section.offsetHeight;
      if (currentHeight + sectionHeight > availableHeight) {
        if (currentPageSections.length > 0) newPages.push([...currentPageSections]);
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
      if (index === sections.length - 1 && currentPageSections.length > 0) newPages.push([...currentPageSections]);
    });
    
    if (newPages.length === 0) newPages.push([]);
    setPages(newPages);
  }, [page.height, topBottomMargin, rightSectionOrder, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font, color]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      paginateSidebarContent();
      paginateMainContent();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [paginateSidebarContent, paginateMainContent, educationEntries, savedSkills, savedSummary, workExperiences, savedAdditionalInfo, savedLanguages,savedWebsites, savedAccomplishments, savedCertifications, savedReferences, softwareSkills, savedVolunteering, savedInterests, customSections, leftSectionOrder, rightSectionOrder, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font, color]);

  const addFontToHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';
    
    const baseStyles = `font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word; overflow-wrap: break-word;`;
    const spanStyles = `font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px;`;
    
    // For divs - preserve existing font-size if present
    htmlContent = htmlContent.replace(/<div([^>]*?)style="([^"]*)"/g, (match, before, style) => {
      if (style.includes('font-size:')) {
        const newStyle = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word; overflow-wrap: break-word; ${style}`;
        return `<div${before}style="${newStyle}"`;
      } else {
        const newStyle = `${baseStyles}${style}`;
        return `<div${before}style="${newStyle}"`;
      }
    });
    htmlContent = htmlContent.replace(/<div(?![^>]*style)([^>]*)>/g, `<div$1style="${baseStyles}">`);
    
    // For p tags - preserve existing font-size if present
    htmlContent = htmlContent.replace(/<p([^>]*?)style="([^"]*)"/g, (match, before, style) => {
      if (style.includes('font-size:')) {
        const newStyle = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word; overflow-wrap: break-word; ${style}`;
        return `<p${before}style="${newStyle}"`;
      } else {
        const newStyle = `${baseStyles}${style}`;
        return `<p${before}style="${newStyle}"`;
      }
    });
    htmlContent = htmlContent.replace(/<p(?![^>]*style)([^>]*)>/g, `<p$1style="${baseStyles}">`);
    
    // For span tags - preserve existing font-size if present
    htmlContent = htmlContent.replace(/<span([^>]*?)style="([^"]*)"/g, (match, before, style) => {
      if (style.includes('font-size:')) {
        const newStyle = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; ${style}`;
        return `<span${before}style="${newStyle}"`;
      } else {
        const newStyle = `${spanStyles}${style}`;
        return `<span${before}style="${newStyle}"`;
      }
    });
    htmlContent = htmlContent.replace(/<span(?![^>]*style)([^>]*)>/g, `<span$1style="${spanStyles}">`);
    
    return htmlContent;
  };

  const totalPages = Math.max(sidebarPages.length, pages.length, 1);

  return (
    <Box sx={{ width: "100%", position: "relative", m: 5 }}>
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap', '@media print': { display: 'none' } }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} color="primary">
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 2 }}>Page {currentPage + 1} of {totalPages}</Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage === totalPages - 1} color="primary">
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 2 }}>
              <Button onClick={() => setArrangeDialogOpen(true)} variant="contained" startIcon={<Reorder />} color="primary">
                Arrange Sections
              </Button>
              <Button onClick={resetAllSectionOrder} variant="outlined" color="secondary" size="small">
                Reset All Sections
              </Button>
            </Box>
          )}
        </Box>
      )}
      
      {/* Hidden measurement containers */}
      <Box ref={sidebarContentRef} sx={{ visibility: 'hidden', position: 'absolute', top: -9999, left: 0, width: "30%", ...globalFontStyle }}>
        {renderSidebarSections()}
      </Box>
      <Box ref={rightContentRef} sx={{ visibility: 'hidden', position: 'absolute', top: -9999, left: 0, width: "62%", ...globalFontStyle }}>
        {renderRightContentSections()}
      </Box>
      
      {/* Visible resume pages */}
      <Box id="resume-container">
        {Array.from({ length: totalPages }).map((_, pageIndex) => (
          <Box
            key={pageIndex}
            className="resume-page"
            sx={{
              width: page.width,
              minHeight: page.height,
              mx: "auto",
              bgcolor: "#fff",
              boxShadow: pageIndex === currentPage ? 3 : 0,
              ...globalFontStyle,
              display: exporting ? 'flex' : (pageIndex === currentPage ? 'flex' : 'none'),
              flexDirection: "column",
              pageBreakAfter: "always",
              boxSizing: "border-box",
              overflow: "hidden",
              "@media print": { display: 'flex', width: page.width, minHeight: page.height, boxShadow: "none", m: 0 },
              '@media print': { display: 'flex' }
            }}
          >
            <Box sx={{ 
              width: "100%", flex: 1,
              p: `${topBottomMargin}px ${sideMargins}px`,
              pt: pageIndex > 0 ? `${sectionSpacing}px` : `${topBottomMargin}px`,
              boxSizing: "border-box",
              overflow: "hidden",
              ...globalFontStyle
            }}>
              {/* Header - only on first page */}
              {pageIndex === 0 && (
                <Box sx={{
                  bgcolor: color,
                  borderRadius: 3,
                  p: 2.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 2.5,
                  color: "#fff",
                  mb: 3,
                  overflow: "hidden",
                  boxSizing: "border-box",
                  width: "100%",
                }}>
                  <Avatar
                    src={photo || "https://via.placeholder.com/150"}
                    sx={{ width: 80, height: 80, flexShrink: 0 }}
                  />
                  <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                    <Typography variant="h5" fontWeight={700} sx={{ fontFamily: font, wordBreak: "break-word" }}>
                      {data.firstName} {data.lastName}
                    </Typography>
                    <Typography fontSize="13px" sx={{ opacity: 0.9, fontFamily: font, fontStyle: fontStyle, wordBreak: "break-word" }}>
                      {data.currentPosition}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Grid container spacing={3} sx={{ flexWrap: "nowrap" }}>
                {/* LEFT COLUMN - 30% */}
                <Grid item sx={{ width: "30%", flexShrink: 0, minWidth: 0, overflow: "hidden" }}>
                  {sidebarPages[pageIndex] ? (
                    <Box
                      sx={{ wordBreak: "break-word", overflowWrap: "break-word", overflow: "hidden", ...globalFontStyle }}
                      dangerouslySetInnerHTML={{ __html: addFontToHtmlContent(sidebarPages[pageIndex].join('')) }}
                    />
                  ) : pageIndex === 0 && (
                    <Typography sx={{ ...textStyle }}>Loading...</Typography>
                  )}
                </Grid>

                {/* RIGHT COLUMN - 62% */}
                <Grid item sx={{ width: "62%", flexShrink: 0, minWidth: 0, overflow: "hidden" }}>
                  {pages[pageIndex] ? (
                    <Box
                      sx={{ wordBreak: "break-word", overflowWrap: "break-word", overflow: "hidden", ...globalFontStyle }}
                      dangerouslySetInnerHTML={{ __html: addFontToHtmlContent(pages[pageIndex].join('')) }}
                    />
                  ) : pageIndex === 0 && (
                    <Typography sx={{ ...textStyle }}>No content for this page</Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Drag and drop sections to reorder or move between left sidebar and right content.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3 }}>
            <Box sx={{ flex: 1 }}>{renderDragPanel("left", leftSectionOrder)}</Box>
            <Box sx={{ flex: 1 }}>{renderDragPanel("right", rightSectionOrder)}</Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAllSectionOrder} color="secondary">Reset All</Button>
          <Button onClick={() => setArrangeDialogOpen(false)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume36;